import EditorJs from '@editorjs/editorjs';

import uuid from 'uuid';
import { PasteListenerParams, EditorData, Block } from './types';
import {
  InstructionAttribute,
  InstructionCSSClasses,
  InstructionEvent,
} from '../../../components/script-editor/tools/types';
import { InstructionType, ScriptInstructions } from '../../../types/instructions';

/* eslint-disable lines-between-class-members */

class PasteListener {
  private editorFormat = 'application/x-editor-js';
  protected editor: EditorJs;
  protected refEditor: HTMLDivElement;
  protected bufferInstructions: any[] = [];

  constructor({ editor, refEditor }: PasteListenerParams) {
    this.editor = editor;
    this.refEditor = refEditor;

    this.bind();
  }

  init() {
    this.refEditor.addEventListener('paste', this.process, true);
  }

  destroy() {
    this.refEditor.removeEventListener('paste', this.process, true);
  }

  protected bind() {
    this.process = this.process.bind(this);
    this.pasteEditorFormat = this.pasteEditorFormat.bind(this);
    this.pasteDefaultFormat = this.pasteDefaultFormat.bind(this);
    this.focusAndScrollToCurrentBlock = this.focusAndScrollToCurrentBlock.bind(this);
    this.init = this.init.bind(this);
    this.destroy = this.destroy.bind(this);
  }

  async process(event: ClipboardEvent) {
    if (event.clipboardData) {
      this.editor.emit(InstructionEvent.SET_PREVENT_ON_CHANGE_EDITOR, true);
      event.preventDefault();
      event.stopPropagation();
      try {
        if (event.clipboardData.types.includes(this.editorFormat)) {
          this.pasteEditorFormat(event);
        } else {
          this.pasteDefaultFormat(event);
        }
      } catch (e) {
        console.error(e);
      }
      this.editor.emit(InstructionEvent.SET_PREVENT_ON_CHANGE_EDITOR, false);
      this.editor.emit(InstructionEvent.SUBMIT_DATA, {});
    }
  }
  protected processInlineInstructionByBlock(block: Block, instructions: ScriptInstructions) {
    const htmlBlock = document.createElement('div');
    htmlBlock.innerHTML = block.data.text;
    const inlineInstructions = htmlBlock.querySelectorAll(`.${InstructionCSSClasses.INLINE_INSTRUCTION_WRAPPER}`);

    inlineInstructions.forEach((element) => {
      const instructionId = element.getAttribute(InstructionAttribute.instructionId);
      if (instructionId) {
        const newInstructionId = uuid();
        const existInstruction = instructions.find(
          (item: InstructionType) => item?.id === instructionId,
        ) as InstructionType;
        if (existInstruction) {
          existInstruction.id = newInstructionId;
          block.data.text = block.data.text.replace(instructionId, newInstructionId);
          this.bufferInstructions.push(existInstruction);
        }
      }
    });
  }
  protected findAndChangeInstructionId(blocks: Block[], instructions: ScriptInstructions, blockIndex?: number) {
    blocks.forEach((block) => {
      const newBlock = { ...block };
      const instructionIdByBlock = newBlock.data.instructionId;
      if (instructionIdByBlock) {
        const newInstructionId = uuid();
        const existInstruction = instructions.find((instruction) => instruction.id === instructionIdByBlock);
        if (existInstruction) {
          existInstruction.id = newInstructionId;
          newBlock.data.instructionId = newInstructionId;
          this.bufferInstructions.push(existInstruction);
        }
      } else {
        this.processInlineInstructionByBlock(newBlock, instructions);
      }
      this.editor.blocks.insert(
        newBlock.type,
        newBlock.data,
        undefined,
        blockIndex
      );
    });
  }

  pasteEditorFormat(event: ClipboardEvent) {
    // @ts-ignore
    const { blocks, instructions } = JSON.parse(event.clipboardData.getData(this.editorFormat)) as EditorData;
    const currentIndexSelectedBlock = this.editor.blocks.getCurrentBlockIndex();
    const currentBlock = this.editor.blocks.getBlockByIndex(currentIndexSelectedBlock);
    if (blocks && instructions) {
      this.editor.blocks.delete(currentIndexSelectedBlock);
      this.findAndChangeInstructionId(blocks, instructions);
      if(currentIndexSelectedBlock === 0 && currentBlock.isEmpty){
        this.editor.blocks.delete(currentIndexSelectedBlock);
      }
      this.editor.emit(InstructionEvent.ADD_INSTRUCTIONS_TO_DATA, this.bufferInstructions);
      this.editor.emit(InstructionEvent.UPDATE_INLINE_ACTION_EVENT_LISTENERS, {});
    }
  }

  pasteDefaultFormat(event: ClipboardEvent) {
    // @ts-ignore
    const text = event.clipboardData.getData('text/plain');
    const arrayText = text.split('\n').filter((item) => item.trim() !== '');
    const currentBlock = this.editor.blocks.getBlockByIndex(this.editor.blocks.getCurrentBlockIndex());

    if (arrayText.length > 1) {
      if (currentBlock.isEmpty) {
        const content = arrayText.shift();
        if (content) {
          const paragraph = currentBlock.holder.querySelector('.ce-paragraph');
          if (paragraph) {
            paragraph.innerHTML += content;
          }
        }

        arrayText.forEach((item) => {
          this.editor.blocks.insert(
            'paragraph',
            { text: item },
          );
        });

        this.focusAndScrollToCurrentBlock();
      } else {
        window.document.execCommand('insertText', false, arrayText.join(' '));
      }
    } else {
      window.document.execCommand('insertText', false, arrayText[0] || '');
    }
  }

  focusAndScrollToCurrentBlock() {
    const currentBlockIndex = this.editor.blocks.getCurrentBlockIndex();
    this.editor.caret.setToBlock(currentBlockIndex, 'end');
    const currentBlock = this.editor.blocks.getBlockByIndex(currentBlockIndex);
    currentBlock.holder.scrollIntoView({
      inline: 'end',
      block: 'center',
    });
  }
}

export default PasteListener;
